import React, { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../auth-context";

// Icons
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const Header = ({ title = "Dashboard" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          minHeight: 72,
          px: { xs: 2, md: 3 },
        }}
      >
        {/* LEFT: PAGE TITLE */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? "20px" : "22px",
              color: theme.palette.text.primary,
              letterSpacing: "-0.3px",
            }}
          >
            {title}
          </Typography>
          {!isMobile && (
            <Typography
              variant="body2"
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 400,
                mt: 0.5,
                fontSize: "13px"
              }}
            >
              Welcome back, {user?.username || "Admin"}
            </Typography>
          )}
        </Box>

        {/* RIGHT: ACTIONS */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Notifications */}
          {/* <IconButton
            sx={{
              position: "relative",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <Badge
              badgeContent={3}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: 18,
                  minWidth: 18,
                  border: `2px solid ${theme.palette.background.paper}`,
                },
              }}
            >
              <NotificationsNoneOutlinedIcon 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: 22 
                }}
              />
            </Badge>
          </IconButton> */}

          {/* Divider */}
          <Box 
            sx={{ 
              height: 32, 
              width: 1, 
              backgroundColor: alpha(theme.palette.divider, 0.3),
              mx: 0.5 
            }} 
          />

          {/* Profile */}
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 1.5,
              cursor: "pointer",
              p: 1,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
            onClick={handleMenuOpen}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontSize: "14px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              {user?.username?.charAt(0).toUpperCase() || "A"}
            </Avatar>
            
            {!isMobile && (
              <>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ 
                      fontWeight: 500, 
                      lineHeight: 1.2,
                      color: theme.palette.text.primary
                    }}
                  >
                    {user?.username || "Admin"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      textTransform: "capitalize",
                      fontSize: "11px"
                    }}
                  >
                    {user?.role || "Administrator"}
                  </Typography>
                </Box>
                <KeyboardArrowDownOutlinedIcon 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: 18,
                    transition: "transform 0.2s ease",
                    transform: open ? "rotate(180deg)" : "rotate(0)"
                  }} 
                />
              </>
            )}
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 4,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                "& .MuiMenuItem-root": {
                  py: 1.25,
                  px: 2,
                  fontSize: "14px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem disabled sx={{ opacity: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {user?.username?.charAt(0).toUpperCase() || "A"}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={500}>
                    {user?.username || "Admin User"}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: "11px" }}
                  >
                    {user?.role || "Administrator"}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>

            <Divider sx={{ my: 1 }} />

            <MenuItem 
              onClick={handleLogout}
              sx={{ 
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                },
              }}
            >
              <LogoutOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;